import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import useFetchResponseSuggestionMutation from 'components/domain/review/useFetchResponseSuggestionMutation';
import TextAreaField from 'components/forms/textAreaField/TextAreaField';
import useAppConfig from 'hooks/useAppConfig';
import useNotify from 'hooks/useNotify';
import { LanguageCode } from 'i18n/internationalization.types';
import { AirbnbGuestReview } from 'models/Reviews';
import FieldPlaceholder from 'pages/property/common/FieldPlaceholder';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { RESPONSE_SUGGESTION_MUTATION_KEY } from './ViewAirbnbGuestReviewModal.constants';
import {
  ResponseFieldRowStyled,
  SuggestAResponseColStyled,
} from './ViewAirbnbGuestReviewsModal.styles';

interface ViewAirbnbGuestReviewModalResponseFieldProps {
  disabled: boolean;
  review: AirbnbGuestReview;
}

const ViewAirbnbGuestReviewModalResponseField = ({
  disabled,
  review,
}: ViewAirbnbGuestReviewModalResponseFieldProps) => {
  const { t } = useTranslation();
  const { notifyError } = useNotify();
  const { hasAiEnabled } = useAppConfig();
  const { property } = usePropertySettingsInitData();
  const {
    setValue,
    formState: { isSubmitting },
  } = useFormContext();

  const {
    mutateAsync: fetchResponseSuggestion,
    isLoading: isFetchingResponseSuggestion,
    isSuccess,
  } = useFetchResponseSuggestionMutation({
    mutationKey: [RESPONSE_SUGGESTION_MUTATION_KEY],
    onError: () => {
      notifyError(
        t(
          'pageProperty.reviews.airbnbGuest.guestReviews.modal.suggestResponseError',
        ),
      );
    },
  });

  const handleSuggestAResponseClick = async () => {
    const suggestedResponse = await fetchResponseSuggestion({
      answerLanguage: LanguageCode.EN,
      guestName: review.guestName,
      locationName: property?.name ?? '',
      reviewContent: review.publicReview,
    });

    if (suggestedResponse.suggestedResponse) {
      setValue('response', suggestedResponse.suggestedResponse, {
        shouldDirty: true,
      });
    }
  };

  return (
    <ResponseFieldRowStyled>
      {isFetchingResponseSuggestion ? (
        <FieldPlaceholder
          colSmLabel={3}
          colSmInput={9}
          label={t(
            'pageProperty.reviews.airbnbGuest.guestReviews.modal.response',
          )}
          linesCount={8.14}
          name="response-placeholder"
          required
        />
      ) : (
        <TextAreaField
          colSmLabel={3}
          colSmInput={9}
          label={t(
            'pageProperty.reviews.airbnbGuest.guestReviews.modal.response',
          )}
          {...(!disabled && {
            maxLength: 1000,
            maxCharacters: 1000,
          })}
          name="response"
          rows={5}
          required
          disabled={disabled || isFetchingResponseSuggestion}
        />
      )}

      {hasAiEnabled && !disabled && (
        <SuggestAResponseColStyled smOffset={3}>
          <Button
            data-testid="suggest-response-button"
            disabled={isFetchingResponseSuggestion || isSubmitting || isSuccess}
            onClick={handleSuggestAResponseClick}
          >
            {t(
              'pageProperty.reviews.airbnbGuest.guestReviews.modal.suggestResponse',
            )}
          </Button>
        </SuggestAResponseColStyled>
      )}
    </ResponseFieldRowStyled>
  );
};

export default ViewAirbnbGuestReviewModalResponseField;
